import { useEffect, useState } from "react";
import { getBrowsePage } from "services/get-browsePage";
import { getProductsPromosCards } from "services/get-getProductsPromosCards";
import { getBrowseContent } from "services/get-getBrowseContent";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useRecoilState, useSetRecoilState } from "recoil";
import { toastState, overlayState, cartState, browseBannerState, browseBannerMessageState } from "atoms";
import { formatQueryStringWithServices } from "utils";
import FeaturedProducts from "components/Browse/FeaturedProducts";
import HiddenProducts from "components/Browse/HiddenProducts";
import { scheduleState } from "atoms";
import { ContentCardModel, BrowseBannerModel, ResponseObject } from "atoms/models";
import INT_ICON_URL from "images/internet-icon.png";
import VID_ICON_URL from "images/tv-icon.png";
import SEC_ICON_URL from "images/security-icon.png";
import TEL_ICON_URL from "images/phone-icon.png";
import { gtmCheckIfExistingCustomer } from "utils/googleTagManager";
import "components/Browse/browse.scss";

interface ProductPromo {
  coreTypes: string[];
  featuredCards: FeaturedCard[];
  hiddenCards: HiddenCard[];
  error: string;
}

interface FeaturedCard {
  cardId: number;
}

interface HiddenCard {
  cardId: number;
}

function Browse() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [toast, setToast] = useRecoilState(toastState);
  const setShowOverlay = useSetRecoilState(overlayState);
  const setCart = useSetRecoilState(cartState);
  const setBanner = useSetRecoilState<BrowseBannerModel>(browseBannerState);
  const setBannerMessage = useSetRecoilState(browseBannerMessageState);
  const [coreTypes, setCoreTypes] = useState<Array<string>>([]);
  const [schedule, setSchedule] = useRecoilState(scheduleState);
  const [params, setParams] = useState<any>({
    item: "",
    planType: "",
    services: "",
  });
  const [featuredContent, setFeaturedContent] = useState<Array<ContentCardModel>>([
    {
      id: 0,
      name: "",
      contentEcommerce: [],
      serviceOptions: [],
    },
  ]);
  const [hiddenContent, setHiddenContent] = useState<Array<ContentCardModel>>([
    {
      id: 0,
      name: "",
      contentEcommerce: [],
      serviceOptions: [],
    },
  ]);

  const setCardContent = async (productPromoResponse: ProductPromo) => {
    try {
      const fetchBrowseContent = async (cards: FeaturedCard[]) => {
        return Promise.all(
          cards.map(async (card: { cardId: number }) => {
            const browseContent = await getBrowseContent(card.cardId);
            const combineProductDetailWithItsContent = Object.assign({}, card, browseContent?.contentItemEcommerce);
            return combineProductDetailWithItsContent;
          })
        );
      };

      const featuredProductsArray: Array<ContentCardModel> = await fetchBrowseContent(productPromoResponse.featuredCards);
      const hiddenProductsArray: Array<ContentCardModel> = await fetchBrowseContent(productPromoResponse.hiddenCards);

      setFeaturedContent(featuredProductsArray);
      setHiddenContent(hiddenProductsArray);
      setShowOverlay(false);
    } catch (error) {
      setShowOverlay(false);
      setToast({
        ...toast,
        show: true,
        variant: "danger",
        header: "Error",
        message: `Error fetching browseContent: ${error}`,
      });
    }
  };

  useEffect(() => {
    const fetchBrowsePage = async () => {
      // Extracting query parameters
      const item: string | null = searchParams.get("item");
      const type: string | null = searchParams.get("planType");
      const serviceParams = searchParams.getAll("services");

      // Setting parameters and showing overlay
      setParams({ ...params, item, type, services: serviceParams });
      setShowOverlay(true);

      let browseResponse: ResponseObject | undefined;

      try {
        // Formatting services from location search
        const services = formatQueryStringWithServices(location.search);
        browseResponse = await getBrowsePage(type, item, services);
        const productPromoResponse = await getProductsPromosCards(location.search);
        setCoreTypes(productPromoResponse?.coreTypes);

        //If URL parameters produces a product we can't offer the user then set the message for the banner
        if (browseResponse?.ecommerceMessageType === "banner") {
          setBannerMessage(browseResponse.message);
        }

        //If getBrowse call returns Banner data then get the data for the product, combine with content then set the state
        if (browseResponse?._embedded?.["ec:banner"]) {
          const bannerInfo = browseResponse?._embedded?.["ec:banner"];
          const getBannerContentFromProductCatalog = await getBrowseContent(bannerInfo.cardId);
          const combineCardDetailsWithContent = Object.assign({}, bannerInfo, getBannerContentFromProductCatalog);
          setBanner(combineCardDetailsWithContent);
        }

        // Setting cart if available in the response. Also setting the billcyle for the perspective sale
        if (browseResponse?._embedded?.["ec:cart"]) {
          setCart(browseResponse._embedded?.["ec:cart"]);
          setSchedule({ ...schedule, billCycle: browseResponse._embedded?.["ec:cart"].billCycle });
          gtmCheckIfExistingCustomer(browseResponse._embedded?.["ec:cart"]);
        }

        // Navigating to window location if available in the response
        if (browseResponse?.["_links"]?.["ec:windowLocation"]?.href) {
          navigate(browseResponse["_links"]["ec:windowLocation"].href);
        }

        // Setting card content if both featured and hidden cards are available
        if (productPromoResponse?.featuredCards && productPromoResponse?.hiddenCards) {
          setCardContent(productPromoResponse);
        }
      } catch (error) {
        setShowOverlay(false);
        if (browseResponse?.["_links"]?.["ec:windowLocation"]?.href) {
          navigate(browseResponse["_links"]["ec:windowLocation"].href);
        }
        console.log("BROWSE CATCH ERROR: ", error);
      }
    };
    fetchBrowsePage();
    // eslint-disable-next-line
  }, [searchParams, setToast, location.search, navigate, setCart, setShowOverlay]);
  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  const handleCoreTypeSelectionClick = (service: string) => {
    // Check if the service is already selected
    const index = params.services.indexOf(service);
    const newServices: Array<string> = [...params.services];

    if (index === -1) {
      // Add the service
      newServices.push(service);
    } else {
      // Remove the service
      newServices.splice(index, 1);
    }

    // Sort the services in alphabetical order
    newServices.sort();

    // Update URL parameters
    const paramsToUpdate = new URLSearchParams(location.search);
    paramsToUpdate.delete("services");
    newServices.forEach((service: string) => {
      paramsToUpdate.append("services", service);
    });

    // Retain existing 'type' and 'item' parameters
    const typeParam = params.type;
    const itemParam = params.item;
    if (typeParam) {
      paramsToUpdate.set("type", typeParam);
    }
    if (itemParam) {
      paramsToUpdate.set("item", itemParam);
    }

    navigate(`?${paramsToUpdate.toString()}`, { replace: true });

    // Update state
    setParams({ ...params, services: newServices });
  };

  const setCoreTypeIcon = (coreType: string) => {
    if (coreType === "INT") {
      return INT_ICON_URL;
    } else if (coreType === "VID") {
      return VID_ICON_URL;
    } else if (coreType === "TEL") {
      return TEL_ICON_URL;
    } else if (coreType === "SEC") {
      return SEC_ICON_URL;
    }
  };

  const setCoreTypeTitle = (coreType: string) => {
    if (coreType === "INT") {
      return "Internet";
    } else if (coreType === "VID") {
      return "TV";
    } else if (coreType === "TEL") {
      return "Phone";
    } else if (coreType === "SEC") {
      return "Security";
    }
  };

  return (
    <div className="browse-container">
      <h1 className="section-title">Plans in your area</h1>
      <h2 className="sub-section-title mb-5">Shop Internet, TV, Security &amp; Phone Plans.</h2>
      <div className="service-boxes-instruction text-center w-100">
        <small>Select service boxes below to show applicable offers.</small>
      </div>
      <div className="core-type-box-container">
        {coreTypes && coreTypes.length > 0
          ? coreTypes.map((coreType: string) => {
              return (
                <div
                  key={coreType}
                  className={params.services.indexOf(coreType) !== -1 ? "box active-box" : "box"}
                  onClick={() => handleCoreTypeSelectionClick(coreType)}
                >
                  <img className="desktop-filter-icon" src={setCoreTypeIcon(coreType)} alt="Comporium Internet" />
                  <p>{setCoreTypeTitle(coreType)}</p>
                </div>
              );
            })
          : null}
      </div>
      <span className="we-suggest-text">
        <strong>SUGGESTED PLANS FOR YOU</strong>
      </span>
      <FeaturedProducts featuredContent={featuredContent} />
      <HiddenProducts hiddenContent={hiddenContent} />
    </div>
  );
}

export default Browse;
